import React, { useState } from "react";

const GOLD = "#AC8E66";
const BG = "#111111";
const SURFACE = "#1A1A1A";
const BORDER = "#2a2a2a";
const TEXT = "#e8e0d4";
const MUTED = "#7a7060";
const APP_URL = "https://zenpost.denisbitter.de";

// ─── kleine Helfer ────────────────────────────────────────────────────────────

function Tag({ children }) {
  return (
    <span
      style={{
        border: `1px solid ${GOLD}`,
        color: GOLD,
        fontSize: "0.65rem",
        padding: "2px 8px",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function Screenshot({ src, alt, caption }) {
  return (
    <figure style={{ margin: 0 }}>
      <div
        style={{
          border: `1px solid ${BORDER}`,
          background: SURFACE,
          overflow: "hidden",
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{ width: "100%", display: "block" }}
          loading="lazy"
        />
      </div>
      {caption && (
        <figcaption
          style={{
            color: MUTED,
            fontSize: "0.7rem",
            marginTop: "8px",
            letterSpacing: "0.06em",
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "48px",
      }}
    >
      <span style={{ color: GOLD, fontSize: "0.7rem", letterSpacing: "0.15em" }}>
        {children}
      </span>
      <span
        style={{
          flex: 1,
          height: "1px",
          background: BORDER,
        }}
      />
    </div>
  );
}

function StudioCard({ number, name, description, features, screenshot, screenshotAlt, reverse }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "64px",
        alignItems: "center",
        direction: reverse ? "rtl" : "ltr",
      }}
      className="studio-grid"
    >
      <div style={{ direction: "ltr" }}>
        <div style={{ color: GOLD, fontSize: "0.65rem", letterSpacing: "0.15em", marginBottom: "16px" }}>
          {number}
        </div>
        <h3 style={{ color: TEXT, fontSize: "1.4rem", margin: "0 0 16px 0", fontWeight: 600 }}>
          {name}
        </h3>
        <p style={{ color: MUTED, lineHeight: 1.7, margin: "0 0 28px 0", fontSize: "0.9rem" }}>
          {description}
        </p>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {features.map((f, i) => (
            <li
              key={i}
              style={{
                color: TEXT,
                fontSize: "0.8rem",
                padding: "8px 0",
                borderBottom: `1px solid ${BORDER}`,
                display: "flex",
                gap: "10px",
                alignItems: "baseline",
              }}
            >
              <span style={{ color: GOLD }}>—</span>
              {f}
            </li>
          ))}
        </ul>
      </div>
      <div style={{ direction: "ltr" }}>
        <Screenshot src={screenshot} alt={screenshotAlt} />
      </div>
    </div>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    q: "Ist ZenPost Studio wirklich kostenlos?",
    a: "Ja, in der aktuellen Beta-Phase ist die App vollständig kostenlos. Alle drei Studios — Converter, Content AI und Doc Studio — sind im Umfang enthalten.",
  },
  {
    q: "Brauche ich einen Cloud-KI-Schlüssel?",
    a: "Nein. Du kannst Ollama lokal nutzen — komplett offline, ohne API-Kosten. Optional kannst du auch OpenAI oder Anthropic einbinden.",
  },
  {
    q: "Welche Plattformen werden unterstützt?",
    a: "LinkedIn, Twitter/X, dev.to, Medium, Reddit, GitHub Discussions und YouTube. Jede Plattform hat eigene Ton- und Formatierungseinstellungen.",
  },
  {
    q: "Was ist der Unterschied zu Hootsuite oder Buffer?",
    a: "ZenPost Studio ist kein Scheduler. Es ist ein Transformations-Werkzeug: Du schreibst deinen Inhalt einmal und das Tool optimiert ihn plattformspezifisch — lokal, ohne Abo.",
  },
  {
    q: "Welche Betriebssysteme werden unterstützt?",
    a: "macOS, Windows und Linux. Die App läuft als native Desktop-Anwendung via Tauri — ca. 3 MB statt 100+ MB (Electron).",
  },
];

function Faq() {
  const [open, setOpen] = useState(null);
  return (
    <div>
      {FAQ_ITEMS.map((item, i) => (
        <div
          key={i}
          style={{
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            style={{
              background: "none",
              border: "none",
              color: TEXT,
              cursor: "pointer",
              width: "100%",
              textAlign: "left",
              padding: "20px 0",
              fontSize: "0.85rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              fontFamily: "monospace",
            }}
          >
            <span>{item.q}</span>
            <span style={{ color: GOLD, flexShrink: 0 }}>{open === i ? "−" : "+"}</span>
          </button>
          {open === i && (
            <div
              style={{
                color: MUTED,
                fontSize: "0.82rem",
                lineHeight: 1.8,
                paddingBottom: "20px",
              }}
            >
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function ZenPostStudioLandingPage() {
  return (
    <div
      style={{
        background: BG,
        color: TEXT,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        minHeight: "100vh",
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .studio-grid {
            grid-template-columns: 1fr !important;
            direction: ltr !important;
            gap: 32px !important;
          }
          .hero-grid {
            grid-template-columns: 1fr !important;
          }
          .problem-grid {
            grid-template-columns: 1fr !important;
          }
          .tech-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 480px) {
          .tech-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "96px 32px 80px",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <Tag>Beta · Kostenlos</Tag>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "64px",
            alignItems: "center",
          }}
          className="hero-grid"
        >
          <div>
            <h1
              style={{
                fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                fontWeight: 700,
                lineHeight: 1.2,
                margin: "0 0 24px 0",
                color: TEXT,
              }}
            >
              Schreibe einmal.
              <br />
              <span style={{ color: GOLD }}>Publiziere überall.</span>
            </h1>
            <p
              style={{
                color: MUTED,
                fontSize: "0.95rem",
                lineHeight: 1.8,
                margin: "0 0 40px 0",
                maxWidth: "420px",
              }}
            >
              ZenPost Studio ist eine native Desktop-App für Entwickler und
              Creator. Konvertiere Inhalte, optimiere sie plattformspezifisch
              mit KI — lokal, offline, ohne Abo.
            </p>

            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: GOLD,
                  color: "#111",
                  padding: "12px 28px",
                  fontSize: "0.8rem",
                  letterSpacing: "0.08em",
                  textDecoration: "none",
                  fontWeight: 700,
                  display: "inline-block",
                }}
              >
                Web-App öffnen →
              </a>
              <a
                href="#studios"
                style={{
                  border: `1px solid ${BORDER}`,
                  color: MUTED,
                  padding: "12px 28px",
                  fontSize: "0.8rem",
                  letterSpacing: "0.08em",
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Features entdecken
              </a>
            </div>
          </div>

          <div>
            <Screenshot
              src="/images/zenpoststudio/wilkkomen.webp"
              alt="ZenPost Studio – Willkommen"
            />
          </div>
        </div>
      </section>

      {/* ── PROBLEM ───────────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: `1px solid ${BORDER}`,
          borderBottom: `1px solid ${BORDER}`,
          background: SURFACE,
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "80px 32px",
          }}
        >
          <SectionLabel>{"// Das Problem"}</SectionLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "40px",
            }}
            className="problem-grid"
          >
            {[
              {
                num: "01",
                title: "Repetitive Anpassungen",
                text: "Du schreibst einen Beitrag und passt ihn manuell für LinkedIn, Twitter, Medium, dev.to an — jedes Mal von vorn.",
              },
              {
                num: "02",
                title: "Laufende KI-Kosten",
                text: "Cloud-KI-Dienste kosten monatlich — ob du sie intensiv nutzt oder nicht. Die Kosten summieren sich.",
              },
              {
                num: "03",
                title: "Datenschutz",
                text: "Deine Texte, Ideen und Projekte landen auf fremden Servern. Lokal zu arbeiten war bisher keine echte Option.",
              },
            ].map((p) => (
              <div key={p.num}>
                <div
                  style={{
                    color: GOLD,
                    fontSize: "0.65rem",
                    letterSpacing: "0.15em",
                    marginBottom: "16px",
                  }}
                >
                  {p.num}
                </div>
                <h3
                  style={{
                    color: TEXT,
                    fontSize: "0.95rem",
                    margin: "0 0 12px 0",
                    fontWeight: 600,
                  }}
                >
                  {p.title}
                </h3>
                <p
                  style={{
                    color: MUTED,
                    fontSize: "0.82rem",
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3 STUDIOS ─────────────────────────────────────────────────────── */}
      <section
        id="studios"
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "96px 32px",
        }}
      >
        <SectionLabel>{"// Die Studios"}</SectionLabel>

        <div style={{ display: "flex", flexDirection: "column", gap: "96px" }}>

          {/* Converter Studio */}
          <StudioCard
            number="Studio 01"
            name="Converter Studio"
            description="Konvertiere Markdown- und Editor.js-Dateien in saubere Ausgabeformate. Mit automatischer Bereinigung von Sonderzeichen und Formatierungsproblemen."
            features={[
              "Markdown → HTML, Plain Text",
              "Editor.js JSON → Markdown",
              "Automatische Formatbereinigung",
              "Datei-basiert, kein Cloud-Upload",
            ]}
            screenshot="/images/zenpoststudio/ZenPost_02_editor.png"
            screenshotAlt="ZenPost Converter Studio – Editor"
          />

          {/* Content AI Studio */}
          <StudioCard
            number="Studio 02"
            name="Content AI Studio"
            description="Transformiere deinen Inhalt plattformspezifisch für 7 Kanäle. Ton, Länge und Zielgruppe sind pro Plattform einstellbar."
            features={[
              "LinkedIn, Twitter/X, dev.to, Medium",
              "Reddit, GitHub Discussions, YouTube",
              "Ton: Professional / Casual / Technical",
              "Lokal via Ollama oder Cloud (OpenAI, Anthropic)",
            ]}
            screenshot="/images/zenpoststudio/contentexport.webp"
            screenshotAlt="ZenPost Content AI – Plattform-Export"
            reverse
          />

          {/* Doc Studio */}
          <StudioCard
            number="Studio 03"
            name="Doc Studio"
            description="Generiere Projektdokumentation direkt aus deiner Codebase. Das Tool analysiert dein Projekt und erstellt standardkonforme Docs."
            features={[
              "README.md generieren",
              "CHANGELOG.md (Keep a Changelog)",
              "API-Dokumentation",
              "CONTRIBUTING.md & Blog-Posts",
            ]}
            screenshot="/images/zenpoststudio/docstudio.png"
            screenshotAlt="ZenPost Doc Studio"
          />
        </div>
      </section>

      {/* ── LOKALE KI ─────────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: `1px solid ${BORDER}`,
          background: SURFACE,
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "96px 32px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "64px",
            alignItems: "center",
          }}
          className="hero-grid"
        >
          <div>
            <SectionLabel>{"// Lokale KI"}</SectionLabel>
            <h2
              style={{
                color: TEXT,
                fontSize: "1.6rem",
                margin: "0 0 20px 0",
                fontWeight: 600,
              }}
            >
              Deine KI.{" "}
              <span style={{ color: GOLD }}>Dein Rechner.</span>
            </h2>
            <p
              style={{
                color: MUTED,
                fontSize: "0.88rem",
                lineHeight: 1.8,
                margin: "0 0 32px 0",
              }}
            >
              Ollama läuft vollständig lokal — keine Daten verlassen deinen
              Rechner. Kein Abo, keine API-Kosten. Wer Cloud-KI bevorzugt:
              OpenAI und Anthropic sind optional einbindbar.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {[
                { label: "Ollama", desc: "Lokal, offline, kostenlos" },
                { label: "OpenAI", desc: "Optional via API-Key" },
                { label: "Anthropic", desc: "Optional via API-Key" },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 0",
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <span style={{ color: TEXT, fontSize: "0.85rem" }}>{item.label}</span>
                  <span style={{ color: MUTED, fontSize: "0.75rem" }}>{item.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <Screenshot
            src="/images/zenpoststudio/lokaleAI.png"
            alt="ZenPost Studio – Lokale KI Einstellungen"
          />
        </div>
      </section>

      {/* ── TECH ──────────────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "80px 32px",
          }}
        >
          <SectionLabel>{"// Tech Stack"}</SectionLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "2px",
            }}
            className="tech-grid"
          >
            {[
              { name: "Tauri 2", note: "~3 MB Binary (kein Electron)" },
              { name: "React 19", note: "TypeScript, Vite 7" },
              { name: "Tailwind v4", note: "Design System" },
              { name: "Ollama", note: "Lokale LLM-Integration" },
            ].map((t) => (
              <div
                key={t.name}
                style={{
                  background: SURFACE,
                  padding: "24px",
                  border: `1px solid ${BORDER}`,
                }}
              >
                <div
                  style={{
                    color: GOLD,
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    marginBottom: "8px",
                  }}
                >
                  {t.name}
                </div>
                <div style={{ color: MUTED, fontSize: "0.72rem" }}>{t.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: `1px solid ${BORDER}`,
          background: SURFACE,
        }}
      >
        <div
          style={{
            maxWidth: "700px",
            margin: "0 auto",
            padding: "96px 32px",
          }}
        >
          <SectionLabel>{"// FAQ"}</SectionLabel>
          <Faq />
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "96px 32px",
            textAlign: "center",
          }}
        >
          <div style={{ marginBottom: "24px" }}>
            <Tag>Open Beta</Tag>
          </div>
          <h2
            style={{
              color: TEXT,
              fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)",
              fontWeight: 700,
              margin: "0 0 20px 0",
            }}
          >
            Bereit zum Starten?
          </h2>
          <p
            style={{
              color: MUTED,
              fontSize: "0.9rem",
              margin: "0 0 48px 0",
              lineHeight: 1.7,
            }}
          >
            ZenPost Studio ist in der Open Beta kostenlos verfügbar.
            <br />
            Die Web-Version läuft direkt im Browser.
          </p>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: GOLD,
              color: "#111",
              padding: "16px 48px",
              fontSize: "0.85rem",
              letterSpacing: "0.1em",
              textDecoration: "none",
              fontWeight: 700,
              display: "inline-block",
            }}
          >
            Zur Web-App →
          </a>
          <div
            style={{
              marginTop: "40px",
              color: MUTED,
              fontSize: "0.7rem",
              letterSpacing: "0.08em",
            }}
          >
            macOS · Windows · Linux · Offline-first · Kein Konto erforderlich
          </div>
        </div>
      </section>
    </div>
  );
}
